import { useMemo } from "react";
import CustomInputSelect from "@/components/desktop/form/input/input-select/CustomInputSelect";
import styles from "./io-form.module.scss";

const RELATION_FIELDS = ["estatesParents", "estates", "categories", "species", "varieties", "quarters", "businessCenters"];

const businessCenterMatchesSelection = (businessCenter, selection) => {
	const hasEstates = selection.estates?.length > 0;
	const hasCategories = selection.categories?.length > 0;
	const hasSpecies = selection.species?.length > 0;
	const hasVarieties = selection.varieties?.length > 0;
	const hasQuarters = selection.quarters?.length > 0;

	if (!hasEstates && !hasCategories && !hasSpecies && !hasVarieties && !hasQuarters) {
		return true;
	}

	const matchesEstates = !hasEstates || businessCenter.estates?.some((estateId) => selection.estates.includes(estateId));
	const matchesCategories = !hasCategories || (businessCenter.category_id && selection.categories.includes(businessCenter.category_id));
	const matchesSpecies = !hasSpecies || businessCenter.species?.some((specieId) => selection.species.includes(specieId));
	const matchesVarieties = !hasVarieties || businessCenter.varieties?.some((varietyId) => selection.varieties.includes(varietyId));
	const matchesQuarters = !hasQuarters || businessCenter.quarters?.some((quarterId) => selection.quarters.includes(quarterId));

	return matchesEstates && matchesCategories && matchesSpecies && matchesVarieties && matchesQuarters;
};

const pruneRelationSelections = (selection, lookupOptions) => {
	const next = { ...selection };

	const allowedEstateIds =
		next.estatesParents?.length > 0
			? lookupOptions.estates.filter((estate) => next.estatesParents.includes(estate.estate_parent_id)).map((estate) => estate.value)
			: lookupOptions.estates.map((estate) => estate.value);

	next.estates = (next.estates || []).filter((estateId) => allowedEstateIds.includes(estateId));

	const allowedCategoryIds =
		next.estates?.length > 0
			? lookupOptions.categories.filter((category) => next.estates.includes(category.estate_id)).map((category) => category.value)
			: [];

	next.categories = (next.categories || []).filter((categoryId) => allowedCategoryIds.includes(categoryId));

	const allowedSpecieIds =
		next.categories?.length > 0 ? lookupOptions.species.filter((specie) => next.categories.includes(specie.category_id)).map((specie) => specie.value) : [];

	next.species = (next.species || []).filter((specieId) => allowedSpecieIds.includes(specieId));

	const allowedVarietyIds =
		next.species?.length > 0 ? lookupOptions.varieties.filter((variety) => next.species.includes(variety.specie_id)).map((variety) => variety.value) : [];

	next.varieties = (next.varieties || []).filter((varietyId) => allowedVarietyIds.includes(varietyId));

	const allowedQuarterIds =
		next.varieties?.length > 0
			? lookupOptions.quarters.filter((quarter) => next.varieties.includes(quarter.variety_id)).map((quarter) => quarter.value)
			: [];

	next.quarters = (next.quarters || []).filter((quarterId) => allowedQuarterIds.includes(quarterId));

	next.businessCenters = (next.businessCenters || []).filter((businessCenterId) => {
		const businessCenter = lookupOptions.businessCenters.find((item) => item.value === businessCenterId);
		return businessCenter && businessCenterMatchesSelection(businessCenter, next);
	});

	return next;
};

const pickRelationFields = (selection) =>
	RELATION_FIELDS.reduce((relationFields, field) => {
		relationFields[field] = selection[field] || [];
		return relationFields;
	}, {});

const IORelations = ({ formData, lookupOptions, onUpdateRelationFields }) => {
	const isApplication = formData.application_type_id === 1;
	const isTransfer = formData.application_type_id === 2;

	const filteredEstates = useMemo(() => {
		if (!formData.estatesParents || formData.estatesParents.length === 0) {
			return lookupOptions.estates;
		}

		return lookupOptions.estates.filter((estate) => formData.estatesParents.includes(estate.estate_parent_id));
	}, [formData.estatesParents, lookupOptions.estates]);

	const filteredCategories = useMemo(() => {
		if (!formData.estates || formData.estates.length === 0) {
			return [];
		}

		return lookupOptions.categories.filter((category) => formData.estates.includes(category.estate_id));
	}, [formData.estates, lookupOptions.categories]);

	const filteredSpecies = useMemo(() => {
		if (!formData.categories || formData.categories.length === 0) {
			return [];
		}

		return lookupOptions.species.filter((specie) => formData.categories.includes(specie.category_id));
	}, [formData.categories, lookupOptions.species]);

	const filteredVarieties = useMemo(() => {
		if (!formData.species || formData.species.length === 0) {
			return [];
		}

		return lookupOptions.varieties.filter((variety) => formData.species.includes(variety.specie_id));
	}, [formData.species, lookupOptions.varieties]);

	const filteredQuarters = useMemo(() => {
		if (!formData.varieties || formData.varieties.length === 0) {
			return [];
		}

		return lookupOptions.quarters.filter((quarter) => formData.varieties.includes(quarter.variety_id));
	}, [formData.varieties, lookupOptions.quarters]);

	const filteredBusinessCenters = useMemo(() => {
		return lookupOptions.businessCenters.filter((businessCenter) => businessCenterMatchesSelection(businessCenter, formData));
	}, [formData, lookupOptions.businessCenters]);

	const applyRelationChange = (field, value) => {
		const prunedSelection = pruneRelationSelections({ ...formData, [field]: value }, lookupOptions);
		onUpdateRelationFields(pickRelationFields(prunedSelection));
	};

	return (
		<div className={styles.section}>
			<div className={styles.fieldsGrid}>
				<CustomInputSelect
					label="Campos"
					name="estatesParents"
					options={lookupOptions.estatesParents}
					value={formData.estatesParents || []}
					onChange={(value) => applyRelationChange("estatesParents", value)}
					isMulti
					col={6}
				/>

				<CustomInputSelect
					label="Bodegas"
					name="estates"
					options={filteredEstates}
					value={formData.estates || []}
					onChange={(value) => applyRelationChange("estates", value)}
					isMulti
					col={6}
				/>

				{isApplication && (
					<>
						<CustomInputSelect
							label="Categorías"
							name="categories"
							options={filteredCategories}
							value={formData.categories || []}
							onChange={(value) => applyRelationChange("categories", value)}
							isMulti
							col={6}
						/>

						<CustomInputSelect
							label="Paso 1"
							name="species"
							options={filteredSpecies}
							value={formData.species || []}
							onChange={(value) => applyRelationChange("species", value)}
							isMulti
							col={6}
						/>

						<CustomInputSelect
							label="Paso 2"
							name="varieties"
							options={filteredVarieties}
							value={formData.varieties || []}
							onChange={(value) => applyRelationChange("varieties", value)}
							isMulti
							col={6}
						/>

						<CustomInputSelect
							label="Paso 3"
							name="quarters"
							options={filteredQuarters}
							value={formData.quarters || []}
							onChange={(value) => applyRelationChange("quarters", value)}
							isMulti
							col={6}
						/>

						<CustomInputSelect
							label="Centros de Costo"
							name="business_centers"
							options={filteredBusinessCenters}
							value={formData.businessCenters || []}
							onChange={(value) => applyRelationChange("businessCenters", value)}
							isMulti
							col={6}
						/>

						<CustomInputSelect
							label="Operadores"
							name="operators"
							options={lookupOptions.operators}
							value={formData.operators || []}
							onChange={(value) => onUpdateRelationFields({ operators: value })}
							isMulti
							col={6}
						/>
					</>
				)}

				{isTransfer && (
					<CustomInputSelect
						label="Transportistas"
						name="hauliers"
						options={lookupOptions.hauliers}
						value={formData.hauliers || []}
						onChange={(value) => onUpdateRelationFields({ hauliers: value })}
						isMulti
						col={6}
					/>
				)}
			</div>
		</div>
	);
};

export default IORelations;
